import deLocale from "@fullcalendar/core/locales/de";
import FullCalendar from "@fullcalendar/react";
import type { CustomButtonInput, EventContentArg, EventInput, EventMountArg, ToolbarInput } from "@fullcalendar/core";
import timeGridPlugin from "@fullcalendar/timegrid";
import * as React from "react";
import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { useGetOne, useRecordContext } from "react-admin";
import { EventContent } from "../../misc/calendar/CalendarEventCardContent";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import {
  autovioCalendarEventToEventInput,
  calendarSlotLabelFormat,
  eventClassNames,
  type PublicHoliday,
  publicHolidayToEventInput,
} from "../../utils/calendar";
import { DrivingSchool } from "../../providers/drivingSchoolsProvider";
import { DateWithoutTime } from "../../model/DateWithoutTime";
import { BKFCourse, type Course, type CourseEvent } from "../../providers/coursesProvider";
import { normalizeStreet } from "../../backoffice.utils";
import { DateTime, Interval } from "luxon";
import { DateRange, dateRangeContains } from "../../model/DateRange";
import { Timetable } from "../../providers/timetablesProvider";
import { Row } from "../../misc/Row";
import { produce } from "immer";
import {
  bulkEditDefaultFormValues,
  BulkEditForm,
  type BulkEditFormValues,
  bulkEditSelectedEventsIdsAtom,
} from "./BulkEditForm";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { CoursePlanningForm, type CoursePlanningFormValues } from "./CoursePlanningForm";
import { useDialog } from "../../hooks/useDialog";
import { AddAppointmentDialog } from "../AddAppointmentDialog/AddAppointmentDialog";
import { currentUser, grants } from "../../backoffice.access_control";
import { FieldErrors, FormProvider, useForm } from "react-hook-form";
import { LocalizedError } from "../../utils/LocalizedError";
import { getAuthenticatedServerClient } from "../../api/server.api";
import { ContextMenu } from "../../misc/ContextMenu";
import { EventDialog } from "../EventDialog";
import { CancelAppointmentDialog } from "../../misc/calendar/CancelAppointmentDialog";
import { DeleteCalendarEventDialog } from "../../misc/calendar/DeleteCalendarEventDialog";
import { ConfirmDialog } from "../ConfirmDialog";
import {
  canBeCanceled,
  canBeDeleted,
  isASFCourseSession,
  isCourseLesson,
  isTheoryLesson,
} from "../../model/autovioCalendarEvents";

type CalendarEventContextMenu = {
  mouseX: number;
  mouseY: number;
  event: CourseEvent;
};

function canEditCourseEvent(event: CourseEvent): boolean {
  if (event.deleted) {
    return false;
  }
  if (isTheoryLesson(event)) {
    return grants.has("calendarEventEdit") || grants.has("TheoryLesson:edit");
  }
  if (isASFCourseSession(event)) {
    return false;
  }
  return grants.has("calendarEventEdit") || (!!event.instructorId && event.instructorId === currentUser?.uid);
}

function canCancelCourseEvent(event: CourseEvent): boolean {
  if (isCourseLesson(event)) {
    // Course lessons are not canceled but deleted ...
    return false;
  }
  return grants.has("calendarEventEdit") && canBeCanceled(event);
}

function canDeleteCourseEvent(event: CourseEvent): boolean {
  if (isCourseLesson(event)) {
    return grants.has("CourseLesson:delete");
  }
  return grants.has("calendarEventEdit") && canBeDeleted(event);
}

export function CourseEventsCalendar(props: { course: Course; initiallyShownDate?: DateWithoutTime }) {
  const { course, initiallyShownDate } = props;
  const { dialogProps: addTheoryLessonDialogProps, openDialog: openAddTheoryLessonDialog } = useDialog();
  const { dialogProps: confirmDeleteCourseLessonDialogProps, openDialog: openConfirmDeleteCourseLessonDialog } =
    useDialog();
  const [mode, setMode] = useState<"coursePlanning" | "bulkEdit">("coursePlanning");
  const [isFreeSlotPreviewHovered, setIsFreeSlotPreviewHovered] = useState(false);
  const [contextMenu, setContextMenu] = useState<CalendarEventContextMenu | null>(null);
  const [eventToEdit, setEventToEdit] = useState<CourseEvent | null>(null);
  const [eventToCancel, setEventToCancel] = useState<CourseEvent | null>(null);
  const [eventToDelete, setEventToDelete] = useState<CourseEvent | null>(null);
  const [courseLessonToDelete, setCourseLessonToDelete] = useState<CourseEvent | null>(null);
  const setSelectedEventsIds = useSetRecoilState(bulkEditSelectedEventsIdsAtom);
  const drivingSchool = useRecordContext<DrivingSchool>();
  const initialDate = useMemo(() => {
    if (initiallyShownDate) {
      return initiallyShownDate.at(8);
    }
    const now = DateTime.now();
    return course.start < now && now < course.end ? now : course.start;
  }, [initiallyShownDate, course]);
  const [dateRange, setDateRange] = useState<DateRange>([
    DateWithoutTime.fromDateTime(initialDate.startOf("week")),
    DateWithoutTime.fromDateTime(initialDate.endOf("week")),
  ]);
  ////////////////////////////////////////////////////////////////
  // We need to memoize *all* properties given to FullCalendar, //
  // to prevent rerendering and therefore flickering ...        //
  ////////////////////////////////////////////////////////////////
  const validRange = useMemo(
    () => ({ start: course.start.toISODate(), end: course.end.plus({ day: 1 }).toISODate() }),
    [course],
  );
  const publicHolidays = useMemo(() => {
    const a = [] as Array<{ name: string; date: DateWithoutTime }>;
    if (!drivingSchool) {
      return a;
    }
    const { location } = course;
    const branch =
      location.type === "OnlineMeeting"
        ? null
        : (() => {
            const normalizedStreet = normalizeStreet(location.street);
            for (const branch of drivingSchool.branches) {
              const branchAddress = branch.postalAddress;
              if (
                normalizedStreet === branchAddress.normalizedStreet &&
                location.postalCode === branchAddress.postalCode
              ) {
                return branch;
              }
            }
          })();
    const knownPublicHolidays = new Set<number>();
    return (branch?.publicHolidays ? [branch] : drivingSchool.branches)
      .flatMap((branch) => branch.publicHolidays ?? [])
      .reduce((acc, publicHoliday) => {
        const { date } = publicHoliday;
        if (knownPublicHolidays.has(date)) {
          return acc;
        }
        knownPublicHolidays.add(date);
        return [...acc, { ...publicHoliday, date: DateWithoutTime.fromNumber(date) }];
      }, a);
  }, [drivingSchool, course]);
  const { data: timetable } = useGetOne<Timetable>(
    "timetables",
    { id: course.timetableId! },
    { enabled: !!course.timetableId },
  );
  const events_ = useMemo(
    () => [
      ...(course.events.map((event) => {
        const eventInput = autovioCalendarEventToEventInput(event);
        eventInput.extendedProps.bulkEdit = {
          onClick: () => {
            setMode("bulkEdit");
            setSelectedEventsIds((selectedEventsIds) =>
              produce(selectedEventsIds, (draft) => {
                if (draft.has(event.id)) {
                  draft.delete(event.id);
                } else {
                  draft.add(event.id);
                }
              }),
            );
          },
        };
        return eventInput;
      }) ?? []),
      ...publicHolidays.map(publicHolidayToEventInput),
    ],
    [course, publicHolidays],
  );
  const blocked = useMemo(() => {
    const blocked: Record</* yyyyMMdd */ number, Array<Interval>> = {};
    events_.forEach((it) => {
      const { event, publicHoliday } = it.extendedProps as { publicHoliday?: PublicHoliday; event?: CourseEvent };
      if (event) {
        if (!dateRangeContains(dateRange, event)) {
          return;
        }
        const { start, end } = event;
        const yyyyMMdd = DateWithoutTime.fromDateTime(start).valueOf();
        const a = blocked[yyyyMMdd];
        if (a) {
          a.push(Interval.fromDateTimes(start, end));
        } else {
          blocked[yyyyMMdd] = [Interval.fromDateTimes(start, end)];
        }
        if (end.day !== start.day) {
          const yyyyMMdd = DateWithoutTime.fromDateTime(end).valueOf();
          const a = blocked[yyyyMMdd];
          if (a) {
            a.push(Interval.fromDateTimes(start, end));
          } else {
            blocked[yyyyMMdd] = [Interval.fromDateTimes(start, end)];
          }
        }
      } else if (publicHoliday) {
        const { date } = publicHoliday;
        blocked[date.valueOf()] = [Interval.fromDateTimes(date.toDateTime(), date.nextDay.toDateTime())];
      }
    });
    return blocked;
  }, [dateRange, events_]);
  const events = useMemo(() => {
    if (mode === "bulkEdit") {
      return events_;
    }
    const freeSlots: Array<EventInput> = [];
    if (timetable) {
      for (let date = dateRange[0]; date <= dateRange[1]; date = date.nextDay) {
        if (date < course.startDate) {
          continue;
        } else if (date > course.endDate) {
          break;
        }
        const { weekday } = date;
        const blocked_ = blocked[date.valueOf()];
        for (const [weekday_, from, to] of timetable.lessonsSchedule) {
          if (weekday_ !== weekday) {
            continue;
          }
          const start = date.at(from);
          const end = date.at(to);
          const slot = Interval.fromDateTimes(start, end);
          let blocked = false;
          if (blocked_) {
            for (const b of blocked_) {
              if (slot.overlaps(b)) {
                blocked = true;
                break;
              }
            }
          }
          if (!blocked) {
            freeSlots.push({
              id: `free_slot_on_${date}_from_${from}_to_${to}`,
              title: "Freier Slot",
              start: slot.start.toISO(),
              end: slot.end.toISO(),
              className: "free-slot",
              extendedProps: { course, start, end, onPreviewHoverChange: setIsFreeSlotPreviewHovered },
            });
          }
        }
      }
    }
    return freeSlots.concat(events_);
  }, [dateRange, events_, timetable, blocked, mode]);
  const minHour = course.events.reduce((acc, it) => Math.min(acc, it.start.hour), 6) ?? 6;
  const plugins = useMemo(() => [timeGridPlugin], []);
  const locales = useMemo(() => [deLocale], []);
  const headerToolbar = useMemo<ToolbarInput>(() => {
    const left = "title";
    let right = "prev today next";
    if (mode === "coursePlanning" && course.isTheoryCourse && grants.has("TheoryLesson:create")) {
      right = "addTheoryLessonButton " + right;
    }
    return { left, center: "", right };
  }, [mode, course.isTheoryCourse]);
  const customButtons = useMemo<{ [name: string]: CustomButtonInput }>(
    () => ({
      addTheoryLessonButton: {
        text: "＋\u00A0Lektion\u00A0\u00A0",
        click: openAddTheoryLessonDialog,
      },
    }),
    [],
  );
  const renderEventContent = useCallback(
    (eventInfo: EventContentArg) => (
      <EventContent eventInfo={eventInfo} mode={mode === "bulkEdit" ? "bulkEdit" : "CourseEventsCalendar"} />
    ),
    [mode],
  );
  const handleEventDidMount = useCallback((eventInfo: EventMountArg) => {
    const event = eventInfo.event.extendedProps.event as CourseEvent | undefined;
    if (!event) {
      return;
    }
    eventInfo.el.oncontextmenu = (mouseEvent) => {
      mouseEvent.preventDefault();
      setContextMenu({ mouseX: mouseEvent.clientX, mouseY: mouseEvent.clientY, event });
    };
  }, []);
  const handleEventWillUnmount = useCallback((eventInfo: EventMountArg) => {
    eventInfo.el.oncontextmenu = null;
  }, []);
  const closeContextMenu = () => setContextMenu(null);
  const handleEditEvent = () => {
    if (!contextMenu) {
      return;
    }
    const { event } = contextMenu;
    closeContextMenu();
    setEventToEdit(event);
  };
  const handleCancelEvent = () => {
    if (!contextMenu) {
      return;
    }
    const { event } = contextMenu;
    closeContextMenu();
    setEventToCancel(event);
  };
  const handleDeleteEvent = () => {
    if (!contextMenu) {
      return;
    }
    const { event } = contextMenu;
    closeContextMenu();
    if (isCourseLesson(event)) {
      setCourseLessonToDelete(event);
      openConfirmDeleteCourseLessonDialog();
    } else {
      setEventToDelete(event);
    }
  };

  if (!(drivingSchool && (!course.timetableId || timetable))) {
    return <LoadingIndicator />;
  }

  const calendar = (
    <div className="CourseEventsCalendar">
      <FullCalendar
        timeZone="Europe/Berlin"
        locales={locales}
        height="auto"
        slotMinTime={`0${minHour}:00:00`}
        slotLabelFormat={calendarSlotLabelFormat}
        plugins={plugins}
        eventMinHeight={1}
        headerToolbar={headerToolbar}
        customButtons={customButtons}
        initialDate={initialDate.toISO()}
        validRange={validRange}
        initialView="timeGridWeek"
        scrollTime="08:00:00"
        allDaySlot={false}
        editable={false}
        weekNumbers={true}
        showNonCurrentDates={false}
        dayMaxEvents={true}
        nowIndicator={true}
        events={events}
        eventClassNames={eventClassNames}
        eventContent={renderEventContent}
        eventDidMount={handleEventDidMount}
        eventWillUnmount={handleEventWillUnmount}
        datesSet={(dates) => {
          const start = DateWithoutTime.fromDateTime(DateTime.fromJSDate(dates.start, { zone: "Europe/Berlin" }));
          const end = DateWithoutTime.fromDateTime(DateTime.fromJSDate(dates.end, { zone: "Europe/Berlin" })).prevDay;
          if (dateRange[0].toString() !== start.toString() || dateRange[1].toString() !== end.toString()) {
            setDateRange([start, end]);
          }
        }}
      />
      <ContextMenu
        open={
          !!contextMenu &&
          (canEditCourseEvent(contextMenu.event) ||
            canCancelCourseEvent(contextMenu.event) ||
            canDeleteCourseEvent(contextMenu.event))
        }
        onClose={closeContextMenu}
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        {contextMenu && canEditCourseEvent(contextMenu.event) && (
          <ContextMenu.Item onClick={handleEditEvent}>Termin bearbeiten</ContextMenu.Item>
        )}
        {contextMenu && canCancelCourseEvent(contextMenu.event) && (
          <ContextMenu.Item onClick={handleCancelEvent}>Termin absagen</ContextMenu.Item>
        )}
        {contextMenu && canDeleteCourseEvent(contextMenu.event) && (
          <ContextMenu.Item onClick={handleDeleteEvent}>Termin löschen</ContextMenu.Item>
        )}
      </ContextMenu>
      {eventToEdit && (
        <EventDialog event={eventToEdit} isOpen onClose={() => setEventToEdit(null)} initialMode="edit" />
      )}
      {eventToCancel && (
        <CancelAppointmentDialog isOpen calendarEvent={eventToCancel} onClose={() => setEventToCancel(null)} />
      )}
      {eventToDelete && (
        <DeleteCalendarEventDialog isOpen calendarEvent={eventToDelete} onClose={() => setEventToDelete(null)} />
      )}
      {courseLessonToDelete && (
        <ConfirmDialog
          {...confirmDeleteCourseLessonDialogProps}
          title="Termin löschen?"
          message="Bist du dir sicher, dass der Termin gelöscht werden soll? Diese Aktion kann nicht rückgängig gemacht werden."
          confirmButtonLabel="Ja, Termin löschen"
          onConfirm={async () => {
            await runSafely(() => dataProvider.delete("courseLessons", { id: courseLessonToDelete.id }), {
              errorMessage: "Fehler beim Löschen des Termins.",
              successMessage: "Termin erfolgreich gelöscht.",
              onSuccess: () => {
                confirmDeleteCourseLessonDialogProps.onClose();
                setCourseLessonToDelete(null);
              },
            });
          }}
        />
      )}
    </div>
  );

  if (course.isBKFCourse) {
    return (
      <_BKFCourseView
        course={course}
        mode={mode}
        calendar={calendar}
        isFreeSlotPreviewHovered={isFreeSlotPreviewHovered}
        cancelBulkEdit={() => {
          setSelectedEventsIds(new Set());
          setMode("coursePlanning");
        }}
      />
    );
  } else if (course.isTheoryCourse) {
    return (
      <>
        {calendar}
        <AddAppointmentDialog {...addTheoryLessonDialogProps} courseId={course.id} tabs={["theoryLesson"]} />
      </>
    );
  } else {
    return calendar;
  }
}

/**
 * Renders the form and calendar with a stable component tree, so that the `FullCalendar`
 * is not unmounted/remounted when switching between "coursePlanning" and "bulkEdit" mode (APP-6232).
 */
function _BKFCourseView(props: {
  course: BKFCourse;
  mode: "coursePlanning" | "bulkEdit";
  calendar: ReactElement;
  isFreeSlotPreviewHovered: boolean;
  cancelBulkEdit: () => void;
}) {
  const { course, mode, calendar, isFreeSlotPreviewHovered, cancelBulkEdit } = props;
  const coursePlanningFormProps = useForm<CoursePlanningFormValues>({
    defaultValues: {
      module: null,
      lecturerId: null,
      roomId: null,
      noteForParticipants: "",
    },
    resolver: (formValues) => {
      const errors: FieldErrors<CoursePlanningFormValues> = {};
      if (!formValues.module) {
        errors.module = {
          type: "required",
          message: "Bitte wähle den Kursteil aus, für den ein Termin erstellt werden soll.",
        };
      }
      return Object.keys(errors).length === 0 ? { values: formValues, errors: {} } : { values: {}, errors };
    },
  });
  const onCoursePlanningFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    return false;
  };

  const bulkEditFormProps = useForm<BulkEditFormValues>({ defaultValues: bulkEditDefaultFormValues });
  const selectedEventsIds = useRecoilValue(bulkEditSelectedEventsIdsAtom);
  const bulkEdit = async (data: BulkEditFormValues) => {
    const numSelectedEvents = selectedEventsIds.size;
    if (numSelectedEvents === 0) {
      showErrorToast("Bitte wähle die Termine aus, die geändert werden sollen.");
      return;
    }
    const { lecturerId, roomId, noteForParticipants } = data;
    if (lecturerId === "__DO_NOT_CHANGE__" && roomId === "__DO_NOT_CHANGE__" && !noteForParticipants) {
      showErrorToast("Bitte gib einen Dozent, Raum oder Hinweis für Teilnehmer ein.");
      return;
    }
    await runSafely(
      async () => {
        const serverClient = await getAuthenticatedServerClient();
        let numChangedEvents = 0;
        // Change events in chronological order ...
        for (const event of course.events
          .filter((it) => selectedEventsIds.has(it.id))
          .sort((a, b) => a.start.valueOf() - b.start.valueOf())) {
          try {
            await serverClient.editEvent_v3(
              {
                type: "CourseLesson",
                ...(lecturerId === "__DO_NOT_CHANGE__" ? {} : { lecturerId: lecturerId || null }),
                ...(roomId === "__DO_NOT_CHANGE__" ? {} : { roomId: roomId || null }),
                ...(noteForParticipants ? { noteForParticipants } : {}),
              },
              { params: { eventId: event.id } },
            );
            ++numChangedEvents;
          } catch (error) {
            console.error("Error during bulk edit", error);
            if (numChangedEvents === 0) {
              throw error;
            } else {
              throw new LocalizedError(
                numChangedEvents === 1
                  ? `Es wurde nur 1 von ${numSelectedEvents} Terminen geändert.`
                  : `Es wurden nur ${numChangedEvents} von ${numSelectedEvents} Terminen geändert.`,
              );
            }
          }
        }
      },
      {
        errorMessage: "Fehler beim Speichern",
        successMessage: "Die ausgewählten Termine wurden erfolgreich geändert.",
        onSuccess: () => {
          cancelBulkEdit();
          bulkEditFormProps.reset(bulkEditDefaultFormValues);
        },
      },
    );
  };
  const onBulkEditFormSubmit = bulkEditFormProps.handleSubmit(bulkEdit);
  const resetBulkEditForm = bulkEditFormProps.reset;

  useEffect(() => {
    if (mode === "bulkEdit" && selectedEventsIds.size === 0) {
      resetBulkEditForm(bulkEditDefaultFormValues, { keepIsSubmitted: false });
      cancelBulkEdit();
    }
  }, [cancelBulkEdit, mode, resetBulkEditForm, selectedEventsIds.size]);

  const formProps: any = mode === "coursePlanning" ? coursePlanningFormProps : bulkEditFormProps;
  const onSubmit = mode === "coursePlanning" ? onCoursePlanningFormSubmit : onBulkEditFormSubmit;
  const form =
    mode === "coursePlanning" ? (
      <CoursePlanningForm course={course} isFreeSlotPreviewHovered={isFreeSlotPreviewHovered} />
    ) : (
      <BulkEditForm course={course} cancelBulkEdit={cancelBulkEdit} />
    );

  return (
    <FormProvider {...formProps}>
      <form onSubmit={onSubmit}>
        <FormAndCalendarLayout form={form} calendar={calendar} />
      </form>
    </FormProvider>
  );
}

export function FormAndCalendarLayout(props: { form: ReactElement; calendar: ReactElement }) {
  const { form, calendar } = props;
  return (
    <Row gap="30px">
      <div style={{ width: "300px", paddingTop: "60px" }}>{form}</div>
      <div style={{ flex: 1 }}>{calendar}</div>
    </Row>
  );
}
